import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";
import { expenseApiService } from "services/expenseApiService";
import { useSelector } from "react-redux";
import { selectUser } from "store/features/auth/authSlice";

const CreateExpense = () => {
  const loginUser = useSelector(selectUser);

  console.log("login user", loginUser);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null);

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Get current user
  // const currentUser = expenseApiService.getCurrentUser();

  // Get today's date
  const today = new Date();
  const formattedDate = today.toISOString().split('T')[0];
  const dayName = days[today.getDay()];

  // Form state
  const [formData, setFormData] = useState({
    store_name: "",
    paid_by: "",
    date: formattedDate,
    day: dayName,
    note: "",
    goods_with: "",
    total: "",
    items: [],
    qb_admin_id: loginUser?.emp_id || "",
    system_admin_id: loginUser?.id || "",
    receipt: null,
  });

  // New item state
  const [newItem, setNewItem] = useState({
    name: "",
    price: "",
    quantity: "1",
  });

  // Calculate total automatically when items change
  useEffect(() => {
    const calculateTotal = () => {
      const itemsTotal = formData.items.reduce((sum, item) => {
        const price = parseFloat(item.price) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        return sum + (price * quantity);
      }, 0);

      setFormData(prev => ({
        ...prev,
        total: itemsTotal.toFixed(2)
      }));
    };

    calculateTotal();
  }, [formData.items]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    if (field === 'date') {
      const selectedDate = new Date(value);
      const newDayName = days[selectedDate.getDay()];
      setFormData(prev => ({
        ...prev,
        date: value,
        day: newDayName,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // Handle item changes in the table
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value,
    };
    setFormData(prev => ({
      ...prev,
      items: updatedItems,
    }));
  };

  // Handle new item input changes
  const handleNewItemChange = (field, value) => {
    setNewItem(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Add new item to the list
  const addItem = () => {
    if (!newItem.name.trim() || !newItem.price) {
      toast.error("Item name and price are required");
      return;
    }

    const item = {
      name: newItem.name.trim(),
      price: parseFloat(newItem.price) || 0,
      quantity: parseFloat(newItem.quantity) || 1,
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, item],
    }));

    // Reset new item form
    setNewItem({
      name: "",
      price: "",
      quantity: "1",
    });
    setAddingItem(false);
  };

  // Remove item from list
  const removeItem = (index) => {
    const updatedItems = [...formData.items];
    updatedItems.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      items: updatedItems,
    }));
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, receipt: file }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Validate form
  const validateForm = () => {
    if (!formData.store_name.trim()) {
      toast.error("Store name is required");
      return false;
    }
    if (!formData.paid_by.trim()) {
      toast.error("Paid by field is required");
      return false;
    }
    if (formData.items.length === 0) {
      toast.error("Please add at least one item");
      return false;
    }
    if (!formData.total || parseFloat(formData.total) <= 0) {
      toast.error("Total amount must be greater than 0");
      return false;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare FormData
      const formDataToSend = new FormData();

      // Append all fields
      formDataToSend.append("store_name", formData.store_name);
      formDataToSend.append("paid_by", formData.paid_by);
      formDataToSend.append("date", formData.date);
      formDataToSend.append("day", formData.day);
      formDataToSend.append("note", formData.note || "");
      formDataToSend.append("goods_with", formData.goods_with || "");
      formDataToSend.append("total", formData.total);
      formDataToSend.append("qb_admin_id", formData.qb_admin_id);
      formDataToSend.append("system_admin_id", formData.system_admin_id);
      formDataToSend.append("items", JSON.stringify(formData.items));

      if (formData.receipt) {
        formDataToSend.append("receipt", formData.receipt);
      }

      await expenseApiService.createExpense(formDataToSend);

      toast.success("Expense created successfully!");

      // Navigate to expenses list
      setTimeout(() => {
        navigate("/expenses");
      }, 1500);

    } catch (error) {
      console.error("Error creating expense:", error);
      toast.error("Failed to create expense. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      store_name: "",
      paid_by: "",
      date: formattedDate,
      day: dayName,
      note: "",
      goods_with: "",
      total: "",
      items: [],
      qb_admin_id: loginUser?.emp_id || "",
      system_admin_id: loginUser?.id || "",
      receipt: null,
    });
    setReceiptPreview(null);
    setNewItem({
      name: "",
      price: "",
      quantity: "1",
    });
  };

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Expenses" breadcrumbItem="Create New Expense" />

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title mb-4">Create New Expense</h4>

                <form onSubmit={handleSubmit}>
                  {/* Basic Information */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Store Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.store_name}
                          onChange={(e) => handleInputChange('store_name', e.target.value)}
                          required
                          placeholder="Enter store name"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Paid By <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.paid_by}
                          onChange={(e) => handleInputChange('paid_by', e.target.value)}
                          required
                          placeholder="Enter who paid for this expense"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Date and Receipt */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Date <span className="text-danger">*</span></label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.date}
                          onChange={(e) => handleInputChange('date', e.target.value)}
                          required
                        />
                        <div className="mt-2">
                          <span className="badge bg-info">Day: {formData.day}</span>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Receipt (Optional)</label>
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*,.pdf"
                          onChange={handleFileChange}
                        />
                        <small className="text-muted">Upload receipt image or PDF (optional)</small>

                        {receiptPreview && (
                          <div className="mt-2">
                            <div className="alert alert-info">
                              <i className="fas fa-file-image me-2"></i>
                              Receipt uploaded: {formData.receipt.name}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Notes Section */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Notes</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={formData.note}
                          onChange={(e) => handleInputChange('note', e.target.value)}
                          placeholder="Additional notes about this expense..."
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Goods With</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={formData.goods_with}
                          onChange={(e) => handleInputChange('goods_with', e.target.value)}
                          placeholder="Who received the goods or services..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Items Section */}
                  <div className="row mb-3">
                    <div className="col-12">
                      <div className="card border-primary">
                        <div className="card-header bg-primary text-white">
                          <h5 className="mb-0">
                            <i className="fas fa-shopping-cart me-2"></i>
                            Expense Items ({formData.items.length})
                          </h5>
                        </div>
                        <div className="card-body">
                          {/* Items Table */}
                          {formData.items.length > 0 ? (
                            <div className="table-responsive">
                              <table className="table table-bordered table-hover">
                                <thead className="table-light">
                                  <tr>
                                    <th width="5%">#</th>
                                    <th width="40%">Item Name</th>
                                    <th width="20%">Price (৳)</th>
                                    <th width="15%">Quantity</th>
                                    <th width="20%">Total (৳)</th>
                                    <th width="10%">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {formData.items.map((item, index) => (
                                    <tr key={index}>
                                      <td>{index + 1}</td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={item.name}
                                          onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <div className="input-group input-group-sm">
                                          <span className="input-group-text">৳</span>
                                          <input
                                            type="number"
                                            className="form-control"
                                            value={item.price}
                                            onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                            step="0.01"
                                            min="0"
                                          />
                                        </div>
                                      </td>
                                      <td>
                                        <input
                                          type="number"
                                          className="form-control form-control-sm"
                                          value={item.quantity}
                                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                          min="1"
                                        />
                                      </td>
                                      <td className="text-end">
                                        <span className="badge bg-success">
                                          ৳{(item.price * item.quantity).toFixed(2)}
                                        </span>
                                      </td>
                                      <td>
                                        <button
                                          type="button"
                                          className="btn btn-danger btn-sm"
                                          onClick={() => removeItem(index)}
                                        >
                                          <i className="fas fa-trash"></i>
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot>
                                  <tr>
                                    <td colSpan="4" className="text-end">
                                      <strong>Subtotal:</strong>
                                    </td>
                                    <td className="text-end">
                                      <strong className="text-primary">
                                        ৳{formData.total}
                                      </strong>
                                    </td>
                                    <td></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                              <p className="text-muted">No items added yet. Add your first item below.</p>
                            </div>
                          )}

                          {/* Add New Item Form */}
                          {addingItem ? (
                            <div className="border p-3 rounded mt-3">
                              <h6 className="mb-3">Add New Item</h6>
                              <div className="row g-3">
                                <div className="col-md-4">
                                  <label className="form-label">Item Name <span className="text-danger">*</span></label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={newItem.name}
                                    onChange={(e) => handleNewItemChange('name', e.target.value)}
                                    placeholder="Enter item name"
                                  />
                                </div>
                                <div className="col-md-3">
                                  <label className="form-label">Price <span className="text-danger">*</span></label>
                                  <div className="input-group">
                                    <span className="input-group-text">৳</span>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={newItem.price}
                                      onChange={(e) => handleNewItemChange('price', e.target.value)}
                                      placeholder="0.00"
                                      step="0.01"
                                      min="0"
                                    />
                                  </div>
                                </div>
                                <div className="col-md-3">
                                  <label className="form-label">Quantity</label>
                                  <input
                                    type="number"
                                    className="form-control"
                                    value={newItem.quantity}
                                    onChange={(e) => handleNewItemChange('quantity', e.target.value)}
                                    min="1"
                                  />
                                </div>
                                <div className="col-md-2 d-flex align-items-end">
                                  <div className="d-flex gap-2">
                                    <button
                                      type="button"
                                      className="btn btn-success"
                                      onClick={addItem}
                                      disabled={!newItem.name.trim() || !newItem.price}
                                    >
                                      <i className="fas fa-plus me-1"></i> Add
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-secondary"
                                      onClick={() => setAddingItem(false)}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center mt-3">
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => setAddingItem(true)}
                              >
                                <i className="fas fa-plus-circle me-2"></i>
                                Add New Item
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Total Amount Section */}
                  <div className="row mb-3">
                    <div className="col-12">
                      <div className="card border-success">
                        <div className="card-body">
                          <div className="row align-items-center">
                            <div className="col-md-6">
                              <h5 className="mb-0">Total Expense Amount</h5>
                            </div>
                            <div className="col-md-6">
                              <div className="input-group">
                                <span className="input-group-text bg-success text-white">
                                  <i className="fas fa-money-bill-wave"></i>
                                </span>
                                <input
                                  type="text"
                                  className="form-control form-control-lg text-end text-success fw-bold"
                                  value={`৳${formData.total}`}
                                  readOnly
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex justify-content-between">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => navigate("/expenses")}
                          disabled={loading}
                        >
                          <i className="fas fa-arrow-left me-1"></i>
                          Back to Expenses
                        </button>

                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={handleReset}
                            disabled={loading}
                          >
                            <i className="fas fa-redo me-1"></i>
                            Reset Form
                          </button>

                          <button
                            type="submit"
                            className="btn btn-success"
                            disabled={loading || formData.items.length === 0}
                          >
                            {loading ? (
                              <>
                                <i className="fas fa-spinner fa-spin me-1"></i>
                                Creating...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-save me-1"></i>
                                Create Expense
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateExpense;